import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import axios from '@/lib/axios';
import { toast } from 'sonner';
import { MissingDocumentsReportViewer } from '@/components/counsel/MissingDocumentsReportViewer';
import { mostRecentFirst } from '@/utils/formatTimestamp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/brd/document-runs`;

const BrdRunMissingDocs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportViewer, setReportViewer] = useState(null);
  const query = new URLSearchParams(location.search);
  const requestedRun = query.get('run') || query.get('run_id');

  const loadRun = async (id) => {
    try {
      const [runResponse, reportsResponse] = await Promise.all([
        axios.get(`${API}/${id}`),
        axios.get(`${API}/${id}/missing-docs-reports`),
      ]);
      setRun(runResponse.data);
      setReports(mostRecentFirst(reportsResponse.data));
    } catch (error) {
      setRun(null);
      setReports([]);
      toast.error(error.response?.data?.detail || 'Unable to load this Run and its saved reports.');
    }
  };

  useEffect(() => {
    axios.get(API)
      .then(({ data }) => {
        setRuns(mostRecentFirst(data));
        if (requestedRun) loadRun(requestedRun);
      })
      .catch(() => toast.error('Unable to load BRD Runs.'));
  }, []);

  const openReport = async (reportId) => {
    try {
      const { data } = await axios.get(`${API}/${run.run.id}/missing-docs-reports/${reportId}`);
      setReportViewer(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to open the saved Missing Documents report.');
    }
  };

  return <><main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8 sm:py-10" data-testid="brd-run-missing-docs-page">
    <div className="mx-auto max-w-6xl">
      <button type="button" onClick={() => navigate('/dashboard')} className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-950" data-testid="brd-run-missing-docs-back-button"><ArrowLeft className="h-4 w-4" />Dashboard</button>
      <section className="border-b border-slate-200 pb-7">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-700" data-testid="brd-run-missing-docs-eyebrow">Online Builder Opinion</p>
        <h1 className="mt-2 text-4xl font-semibold text-slate-950" data-testid="brd-run-missing-docs-title">Saved Missing Documents Reports</h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-600" data-testid="brd-run-missing-docs-description">Reports are created only by the original Missing Documents Identifier and displayed here without re-analysis or changed findings.</p>
      </section>
      <div className="mt-8 grid gap-6 lg:grid-cols-[18rem_1fr]">
        <aside className="border border-slate-200 bg-white" data-testid="brd-analysis-run-list">
          <h2 className="border-b border-slate-200 p-4 text-sm font-semibold" data-testid="brd-analysis-run-list-title">Run Numbers</h2>
          {runs.map((item) => <button key={item.id} type="button" onClick={() => loadRun(item.id)} className={`w-full border-b border-slate-100 p-4 text-left hover:bg-slate-50 ${run?.run.id === item.id ? 'bg-blue-50' : ''}`} data-testid={`brd-analysis-run-${item.id}`}><p className="font-semibold">{item.run_number}</p><p className="mt-1 text-sm text-slate-600">{item.matter_label}</p></button>)}
        </aside>
        <section>
          {run ? <div className="space-y-6">
            <section className="border border-slate-200 bg-white p-6" data-testid="brd-analysis-selection-panel">
              <p className="font-mono text-sm text-blue-700" data-testid="brd-analysis-run-number">{run.run.run_number}</p>
              <h2 className="mt-2 text-2xl font-semibold" data-testid="brd-analysis-run-matter">{run.run.matter_label}</h2>
              <p className="mt-2 text-sm text-slate-600" data-testid="brd-analysis-selection-hint">{run.documents.filter((document) => document.is_latest).length} current evidence documents. The original Identifier owns analysis and report creation for this Run.</p>
            </section>
            <section className="border border-slate-200 bg-white p-6" data-testid="brd-analysis-reports-panel">
              <h3 className="flex items-center gap-2 font-semibold" data-testid="brd-analysis-reports-title"><Save className="h-5 w-5 text-emerald-700" />Saved report versions</h3>
              {reports.length ? <div className="mt-4 space-y-2">{reports.map((report) => <button key={report.id} type="button" onClick={() => openReport(report.id)} className="flex w-full justify-between border border-slate-200 p-3 text-left hover:bg-slate-50" data-testid={`brd-report-${report.id}`}><span>Version {report.analysis_version}</span><span className="text-sm text-slate-500">{report.selected_document_count} selected documents</span></button>)}</div> : <p className="mt-3 text-sm text-slate-500" data-testid="brd-analysis-empty-reports">No saved report is available for this Run yet. Complete the original Missing Documents Identifier to create one.</p>}
            </section>
          </div> : <div className="flex min-h-80 items-center justify-center border border-dashed border-slate-300 bg-white"><p className="text-slate-600" data-testid="brd-analysis-select-run">Select a Run Number to view its saved reports.</p></div>}
        </section>
      </div>
    </div>
  </main>{reportViewer ? <MissingDocumentsReportViewer report={reportViewer} onBack={() => setReportViewer(null)} /> : null}</>;
};

export default BrdRunMissingDocs;